import { Filters } from './components/Filters';
import { FrontTitle } from './components/FrontTitle';
import { MantineProvider, AppShell, Burger, Group, Modal, Divider, Stack, Button, Loader, Grid, Title, Popover, Text } from '@mantine/core';
import { Highlight } from './components/Highlight';
import { useDisclosure } from '@mantine/hooks';
import { IconBrandYoutube, IconMail, IconCoffee } from '@tabler/icons-react';

import '@mantine/core/styles.css';
import { useState, useEffect } from 'react';
import supabase from './api/client';
import InfiniteScroll from 'react-infinite-scroll-component';
import { createPlayerGroups, createTeamGroups } from './api/helpers';
import "./styles/App.css"
import { RecentGames } from './components/RecentGames';
import { Header } from './components/Header';
import { FilterCol } from './components/FilterCol';

import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';

function App() {
  const [opened, { open, close }] = useDisclosure(false);
  const [filters, setFilters] = useState({
    "event": null,
    "game": null,
    "team": null,
    "player": null
  })

  const [query, setQuery] = useState(null);

  // Static state
  const [allEvents, setAllEvents] = useState(null)
  const [allTeams, setAllTeams] = useState(null)
  const [allPlayers, setAllPlayers] = useState(null)

  const [clips, setClips] = useState([])
  const [hasMore, setHasMore] = useState(true);
  const [index, setIndex] = useState(4);

  async function fetchInitialClips() {
    let collectedData = null

    if (query) {
      const { data } = await query.range(0, 3)
      collectedData = data
    }
    else {
      const { data } = await supabase.from("Highlight").select("id, title, videoId").range(0, 3).order("created_at", { ascending: false })
      collectedData = data
    }

    if (collectedData.length === 0) {
      setHasMore(false)
    }
    else {
      setHasMore(true)
    }

    setClips([...collectedData])
  }

  async function fetchClips() {
    let collectedData = null

    if (query) {
      const { data } = await query.range(index, index + 3)
      collectedData = data
    }
    else {
      const { data } = await supabase.from("Highlight").select("id, title, videoId").range(index, index + 3).order("created_at", { ascending: false })
      collectedData = data
    }

    if (collectedData.length === 0) {
      setHasMore(false)
    }
    else {
      setHasMore(true)
    }

    setClips((prevClips) => [...prevClips, ...collectedData])
    setIndex((prevIndex) => prevIndex + 4)
  }

  async function getAllEvents() {
    const { data } = await supabase.from("Event").select().order("date", { ascending: false })
    const events = data.map(event => ({ ...event, value: (event.id).toString(), label: event.name }))
    setAllEvents(events);
  }

  async function getAllTeams() {
    const { data: teams } = await supabase.from("Team").select()
    setAllTeams(createTeamGroups(teams))
  }

  async function getAllPlayers() {
    const { data } = await supabase.from("Player").select("*, Team (name)")
    setAllPlayers(createPlayerGroups(data))
  }

  const goToYoutube = () => {
    window.location.href = "https://www.youtube.com/@ValorianPlay";
  }

  const goToCoffee = () => {
    window.location.href = "https://buymeacoffee.com/valorantesportsarchive";
  }

  useEffect(() => {
    getAllEvents()
    getAllTeams()
    getAllPlayers()
  }, [])

  useEffect(() => {
    setClips([])
    setIndex(4)
    setHasMore(true)
    fetchInitialClips()
  }, [query])

  return (
    <div className="App">
      <Analytics />
      <SpeedInsights />
      <MantineProvider defaultColorScheme="dark">
        <AppShell
          header={{ height: 60 }}
          styles={{
            root: {
              backgroundColor: "#202020"
            }
          }}
        >
          <AppShell.Header
            styles={{
              header: {
                backgroundColor: "#191919",
                alignContent: "center"
              }
            }}
          >
            <Group justify="flex-end" styles={{ root: { paddingRight: "3%" } }}>
              <Burger
                opened={opened}
                onClick={open}
              />
              <Divider orientation="vertical" />
              <Button
                variant="transparent"
                size="xs"
                radius="xl"
                color='#FF0000'
                onClick={goToYoutube}
                styles={{
                  root: {
                    padding: "0%"
                  }
                }}
              >
                <IconBrandYoutube size={30} />
              </Button>
              <Divider orientation="vertical" />
              <Popover position="bottom" withArrow shadow="md">
                <Popover.Target>
                  <Button
                    variant="transparent"
                    size="xs"
                    radius="xl"
                    color="indigo"
                    styles={{
                      root: {
                        padding: "0%"
                      }
                    }}
                  >
                    <IconMail size={30} />
                  </Button>
                </Popover.Target>
                <Popover.Dropdown>
                  <Text size="xs">valorantesportsarchive@gmail.com</Text>
                </Popover.Dropdown>
              </Popover>
            </Group>
          </AppShell.Header>
          <AppShell.Main>
            <FrontTitle></FrontTitle>
            <Modal
              opened={opened}
              onClose={close}
              title="Filters"
              styles={{
                root: {
                  backgroundColor: "#202020"
                },
                header: {
                  backgroundColor: "#202020"
                },
                content: {
                  padding: "0% 0.5% 0.5% 0.5%",
                  backgroundColor: "#202020"
                }
              }}>
              <Stack>
                <Divider />
                <Filters
                  close={close}
                  setQuery={setQuery}
                  filters={filters}
                  setFilters={setFilters}
                  allEvents={allEvents}
                  allTeams={allTeams}
                  allPlayers={allPlayers}
                />
              </Stack>
            </Modal>
            <RecentGames setQuery={setQuery} setFilters={setFilters} />
            <Header text={query ? "Most Recent" : "Recently Added"}></Header>
            <Grid
              className="filter-grid"
              gutter="xs"
              justify="start"
            >
              {filters.event ? <FilterCol text={filters.event.name} /> : null}
              {filters.game ? <FilterCol text={filters.game.name} /> : null}
              {filters.team ? <FilterCol text={filters.team.name} /> : null}
              {filters.player ? <FilterCol text={filters.player.name} /> : null}
            </Grid>
            <Stack justify='center'>
              <InfiniteScroll
                style={{ textAlign: "center" }}
                dataLength={clips.length}
                next={fetchClips}
                hasMore={hasMore}
                loader={<Loader size={"lg"} styles={{ root: { margin: "2% 0% 2% 0%" } }} />}
                endMessage={!clips.length ?
                  <Title
                    className='front-header'
                    styles={{
                      root: {
                        margin: "0 auto",
                        marginTop: "4%",
                        textAlign: "center"
                      }
                    }}
                  >
                    No Clips Found
                  </Title> : null}
              >
                {clips.length ? clips.map((clip, index) => {
                  if (index === clips.length - 1) {
                    return <><Highlight key={clip.id} id={clip.id} title={clip.title} videoId={clip.videoId} /></>
                  }

                  return <><Highlight key={clip.id} id={clip.id} title={clip.title} videoId={clip.videoId} /><Divider /></>
                }) : null}
              </InfiniteScroll>
            </Stack>
          </AppShell.Main>
        </AppShell>
      </MantineProvider>
    </div>
  );
}

export default App;
